<script setup lang="ts">
  import ChatOptionsMenu from '@/components/app/chat/components/ChatOptionsMenu.vue'
  import ChatHistoryModal from '@/components/app/chat/sidepanel/ChatHistoryModal.vue'
  import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
  import { useChatSessionHistory } from '@/composables/useChatSessionHistory'
  import { useTranslation } from '@/composables/useTranslation'
  import { ChatPreferencesService } from '@/types/generated/services.gen'
  import { ChevronDown, Maximize2, Minimize2, MessagesSquare, SquarePen, X } from 'lucide-vue-next'
  import { onMounted, ref, watch } from 'vue'

  const props = defineProps<{
    sessionTitle?: string | null
    hasActiveSession?: boolean
    hasMessages?: boolean
    isStreaming?: boolean
    isFullscreenOverlay?: boolean
    moveSessionTitle?: string | null
    moveSessionUuid?: string | null
  }>()

  const emit = defineEmits<{
    (e: 'close'): void
    (e: 'expand'): void
    (e: 'collapse'): void
    (e: 'newChat'): void
    (e: 'selectSession', uuid: string): void
    (e: 'usePrompt', prompt: string): void
    (e: 'download-pdf'): void
    (e: 'rename'): void
    (e: 'organize'): void
    (e: 'delete-chat'): void
    (e: 'moveToFolder', data: { sessionUuid: string; folderUuid: string }): void
    (e: 'moveCancelled'): void
  }>()

  const showAllModalOpen = ref(false)
  const modalInitialTab = ref<'chats' | 'folders' | 'prompts' | 'settings'>('chats')
  const popoverOpen = ref(false)
  const settingsCount = ref(0)

  function openModalWithTab(tab: 'chats' | 'folders' | 'prompts' | 'settings') {
    modalInitialTab.value = tab
    showAllModalOpen.value = true
  }

  watch(showAllModalOpen, (isOpen) => {
    if (!isOpen && props.moveSessionTitle) {
      emit('moveCancelled')
    }
  })

  const { t } = useTranslation({
    en: {
      expand: 'Expand',
      collapse: 'Collapse',
      close: 'Close',
      moreOptions: 'More options',
      chatHistory: 'Chat history',
      myChats: 'My Chats',
      teamChats: 'Team Chats',
      showAll: 'Show all',
      newChat: 'New Chat'
    },
    de: {
      expand: 'Vergrößern',
      collapse: 'Verkleinern',
      close: 'Schließen',
      moreOptions: 'Weitere Optionen',
      chatHistory: 'Chat-Verlauf',
      myChats: 'Meine Chats',
      teamChats: 'Team Chats',
      showAll: 'Alle anzeigen',
      newChat: 'Neuer Chat'
    },
    fr: {
      expand: 'Agrandir',
      collapse: 'Réduire',
      close: 'Fermer',
      moreOptions: "Plus d'options",
      chatHistory: 'Historique des chats',
      myChats: 'Mes chats',
      teamChats: "Chats d'équipe",
      showAll: 'Tout afficher',
      newChat: 'Nouveau chat'
    }
  })

  const activeTab = ref<'my' | 'team'>('my')
  const { mySessions, teamSessions, fetchSessions } = useChatSessionHistory({ popoverOpen })

  /**
   * Force a bypass of the session-list cooldown. Parents call this after mutations
   * (rename/delete) so the popover never shows stale entries even when the user
   * reopens it inside the refresh cooldown window.
   */
  const refreshSessions = () => fetchSessions({ force: true })

  defineExpose({ openModalWithTab, refreshSessions })

  function setActiveTab(tab: 'my' | 'team') {
    activeTab.value = tab
  }

  function handleSelectSession(uuid: string) {
    emit('selectSession', uuid)
    popoverOpen.value = false
  }

  function handleShowAll() {
    popoverOpen.value = false
    openModalWithTab('chats')
  }

  onMounted(async () => {
    void fetchSessions()
    try {
      const res = await ChatPreferencesService.chatPreferencesList({ limit: 1 })
      settingsCount.value = res.count ?? 0
    } catch {
      // silently fail
    }
  })
</script>

<template>
  <header class="flex h-14 items-center gap-6 bg-white p-4">
    <Popover v-model:open="popoverOpen">
      <!-- Left section -->
      <div class="flex max-w-36 items-center gap-2">
        <PopoverTrigger as-child>
          <button
            type="button"
            class="flex max-w-36 items-center gap-2 hover:opacity-70"
            :aria-label="t('chatHistory')"
          >
            <!-- Active session: title + chevron -->
            <template v-if="props.hasActiveSession">
              <span
                class="line-clamp-1 text-base font-medium leading-6 font-inter"
                :class="popoverOpen ? 'text-purple-600' : 'text-gray-800'"
              >
                {{ props.sessionTitle || t('newChat') }}
              </span>
              <ChevronDown
                class="h-6 w-6 shrink-0 transition-transform duration-200"
                :class="popoverOpen ? 'rotate-180 text-purple-600' : 'text-gray-800'"
                :stroke-width="1.25"
              />
            </template>

            <!-- No session: messages icon -->
            <MessagesSquare
              v-else
              class="h-6 w-6 text-gray-800"
              :stroke-width="1.25"
            />
          </button>
        </PopoverTrigger>
      </div>

      <PopoverContent
        align="start"
        :side-offset="8"
        class="w-72 overflow-hidden rounded-xl bg-white p-0
          shadow-[0px_4px_8px_0px_rgba(0,0,0,0.08),0px_8px_12px_0px_rgba(0,0,0,0.08)] outline outline-1
          -outline-offset-1 outline-gray-200"
      >
        <!-- Chat list section -->
        <div
          class="flex flex-col items-start justify-start gap-2 self-stretch border-b border-gray-50 p-4"
        >
          <div class="inline-flex items-center gap-2.5 self-stretch">
            <div class="inline-flex flex-1 flex-col items-start justify-start gap-5">
              <!-- Tabs -->
              <div
                class="inline-flex items-start justify-start gap-3 self-stretch"
                role="tablist"
                :aria-label="t('chatHistory')"
              >
                <button
                  type="button"
                  role="tab"
                  :aria-selected="activeTab === 'my'"
                  class="flex flex-1 shrink-0 items-center justify-center gap-2.5 whitespace-nowrap rounded px-5 py-2.5
                    text-sm font-medium leading-5 text-black font-inter"
                  :class="
                    activeTab === 'my'
                      ? 'outline outline-1 -outline-offset-1 outline-gray-200'
                      : 'rounded-[300px]'
                  "
                  @click="setActiveTab('my')"
                >
                  {{ t('myChats') }}
                </button>
                <button
                  type="button"
                  role="tab"
                  :aria-selected="activeTab === 'team'"
                  class="flex flex-1 shrink-0 items-center justify-center gap-2.5 whitespace-nowrap rounded px-5 py-2.5
                    text-sm font-medium leading-5 text-black font-inter"
                  :class="
                    activeTab === 'team'
                      ? 'outline outline-1 -outline-offset-1 outline-gray-200'
                      : 'rounded-[300px]'
                  "
                  @click="setActiveTab('team')"
                >
                  {{ t('teamChats') }}
                </button>
              </div>

              <!-- Session list -->
              <template v-if="activeTab === 'my'">
                <button
                  v-for="session in mySessions"
                  :key="session.uuid"
                  type="button"
                  class="line-clamp-1 self-stretch text-left text-sm font-normal leading-5 text-gray-600 font-inter
                    hover:text-gray-800"
                  @click="handleSelectSession(session.uuid)"
                >
                  {{ session.title }}
                </button>
              </template>
              <template v-else>
                <button
                  v-for="session in teamSessions"
                  :key="session.uuid"
                  type="button"
                  class="line-clamp-1 self-stretch text-left text-sm font-normal leading-5 text-gray-600 font-inter
                    hover:text-gray-800"
                  @click="handleSelectSession(session.uuid)"
                >
                  {{ session.title }}
                </button>
              </template>
            </div>
          </div>
        </div>

        <!-- Show all footer -->
        <div class="flex flex-col items-start justify-center gap-4 self-stretch p-4">
          <div class="flex flex-col items-center justify-center gap-4 self-stretch">
            <button
              type="button"
              class="inline-flex items-center gap-2.5 self-stretch text-left hover:opacity-70"
              :aria-label="t('showAll')"
              @click="handleShowAll()"
            >
              <div class="relative h-5 w-5 overflow-hidden">
                <MessagesSquare
                  class="h-5 w-5 text-gray-800"
                  :stroke-width="1.25"
                />
              </div>
              <div class="text-sm font-normal leading-5 text-gray-800 font-inter">
                {{ t('showAll') }}
              </div>
            </button>
          </div>
        </div>
      </PopoverContent>
    </Popover>

    <div class="h-5 flex-1" />

    <div class="flex items-center gap-6">
      <!-- New chat (only when active session) -->
      <button
        v-if="props.hasActiveSession"
        type="button"
        class="flex h-5 w-5 items-center justify-center text-gray-800 hover:opacity-70"
        :aria-label="t('newChat')"
        @click="emit('newChat')"
      >
        <SquarePen
          class="h-5 w-5"
          :stroke-width="1.25"
        />
      </button>
      <!--
        Expand / collapse stays interactive while a reply is streaming.
        ``chatMode`` is purely a UI-shape flag; flipping it does **not** unmount
        :class:`SidePanelChat` or its ``useChatStream('sidepanel')`` instance, so the
        WebSocket subscription, the streaming assistant message, and any in-flight
        document processing all survive the layout swap. Disabling the button mid-stream
        was a leftover from the legacy router-push handoff (which DID unmount the chat
        surface and therefore had to block expansion until the stream completed).
      -->
      <button
        type="button"
        class="flex h-4 w-4 items-center justify-center text-gray-800 hover:opacity-70"
        :aria-label="props.isFullscreenOverlay ? t('collapse') : t('expand')"
        :data-cy="props.isFullscreenOverlay ? 'ai-overlay-collapse' : 'ai-side-panel-expand'"
        @click="props.isFullscreenOverlay ? emit('collapse') : emit('expand')"
      >
        <Minimize2
          v-if="props.isFullscreenOverlay"
          class="h-4 w-4"
          :stroke-width="1.25"
        />
        <Maximize2
          v-else
          class="h-4 w-4"
          :stroke-width="1.25"
        />
      </button>
      <ChatOptionsMenu
        :has-messages="props.hasMessages ?? false"
        :is-streaming="props.isStreaming"
        :settings-count="settingsCount || undefined"
        hide-pre-chat-settings-count
        @download-pdf="emit('download-pdf')"
        @rename="emit('rename')"
        @organize="emit('organize')"
        @delete="emit('delete-chat')"
        @settings="openModalWithTab('settings')"
      />
      <button
        type="button"
        class="flex h-6 w-6 items-center justify-center text-gray-800 hover:opacity-70"
        :aria-label="t('close')"
        data-cy="ai-side-panel-close"
        @click="emit('close')"
      >
        <X
          class="h-6 w-6"
          :stroke-width="1.25"
        />
      </button>
    </div>
  </header>

  <ChatHistoryModal
    v-model:open="showAllModalOpen"
    :initial-tab="modalInitialTab"
    :move-session-title="moveSessionTitle"
    :move-session-uuid="moveSessionUuid"
    @select-session="emit('selectSession', $event)"
    @use-prompt="emit('usePrompt', $event)"
    @move-to-folder="emit('moveToFolder', $event)"
    @sessions-changed="refreshSessions"
  />
</template>
